import apiClient from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type {
  Patient360ResponseDto,
  PatientSummaryDto,
  Patient360CaseDto,
  Patient360FinancialDto,
  Patient360WorkflowItemDto,
  Patient360CallDto,
  Patient360AdverseEventDto,
  Patient360NotificationItemDto,
  Patient360ClaimDto,
  Patient360DocumentDto,
  PatientActivityTimelineResponse,
  PatientActivityTimelineQuery,
  PatientInsightsResponse,
} from '../types/patient360';
import type {
  ResolvePanelStepsRequest,
  ResolvePanelStepsResponse,
} from '../components/patient360/panels/steps/AdaptivePanelStepResolver';

/**
 * Patient 360 API Service
 * Centralized service for Patient 360 View data fetching.
 * 
 * Architecture:
 * - Single aggregated endpoint for initial load (efficient)
 * - Typed response data with full DTOs
 * - Ready for future granular endpoints if needed
 */

// ─── API Response Types ─────────────────────────────────────

export interface Patient360TimelineDto {
  workflow: Patient360WorkflowItemDto[];
  calls: Patient360CallDto[];
  adverseEvents: Patient360AdverseEventDto[];
  notifications: Patient360NotificationItemDto[];
}

// ─── Main Service ───────────────────────────────────────────

export const Patient360API = {
  /**
   * Get comprehensive patient 360 data (aggregated)
   * This is the primary endpoint - loads all data in one call
   */
  getPatient360: async (patientId: string): Promise<ApiResponse<Patient360ResponseDto>> => {
    const response = await apiClient.get<ApiResponse<Patient360ResponseDto>>(
      `/patients/${patientId}/360-dynamic`
    );
    return response.data;
  },

  /**
   * Get patient basic information
   * Uses the 360-dynamic endpoint and extracts patient data
   */
  getPatient: async (_programId: string, patientId: string): Promise<ApiResponse<PatientSummaryDto>> => {
    const response = await apiClient.get<ApiResponse<Patient360ResponseDto>>(
      `/patients/${patientId}/360-dynamic`
    );
    return {
      success: response.data.success,
      data: response.data.data?.patient,
      message: response.data.message,
      errors: response.data.errors,
      correlationId: response.data.correlationId,
    } as ApiResponse<PatientSummaryDto>;
  },

  /**
   * Get patient's assistance cases
   */
  getCases: async (patientId: string): Promise<ApiResponse<Patient360CaseDto[]>> => {
    const response = await apiClient.get<ApiResponse<Patient360ResponseDto>>(
      `/patients/${patientId}/360-dynamic`
    );
    return {
      success: response.data.success,
      data: response.data.data?.cases || [],
      message: response.data.message,
      errors: response.data.errors,
      correlationId: response.data.correlationId,
    } as ApiResponse<Patient360CaseDto[]>;
  },

  /**
   * Get patient timeline (workflow + calls + adverse events + notifications)
   */
  getTimeline: async (patientId: string): Promise<ApiResponse<Patient360TimelineDto>> => {
    const response = await apiClient.get<ApiResponse<Patient360ResponseDto>>(
      `/patients/${patientId}/360-dynamic`
    );
    return {
      success: response.data.success,
      data: {
        workflow: response.data.data?.workflow || [],
        calls: response.data.data?.calls || [],
        adverseEvents: response.data.data?.adverseEvents || [],
        notifications: response.data.data?.notifications || [],
      },
      message: response.data.message,
      errors: response.data.errors,
      correlationId: response.data.correlationId,
    } as ApiResponse<Patient360TimelineDto>;
  },

  /**
   * Get patient documents
   */
  getDocuments: async (patientId: string): Promise<ApiResponse<Patient360DocumentDto[]>> => {
    const response = await apiClient.get<ApiResponse<Patient360ResponseDto>>(
      `/patients/${patientId}/360-dynamic`
    );
    return {
      success: response.data.success,
      data: response.data.data?.documents || [],
      message: response.data.message,
      errors: response.data.errors,
      correlationId: response.data.correlationId,
    } as ApiResponse<Patient360DocumentDto[]>;
  },

  /**
   * Get patient financial data (utilization + ledger + claims)
   */
  getFinancials: async (patientId: string): Promise<ApiResponse<{
    financial: Patient360FinancialDto | null;
    claims: Patient360ClaimDto[];
  }>> => {
    const response = await apiClient.get<ApiResponse<Patient360ResponseDto>>(
      `/patients/${patientId}/360-dynamic`
    );
    return {
      success: response.data.success,
      data: {
        financial: response.data.data?.financial || null,
        claims: response.data.data?.claims || [],
      },
      message: response.data.message,
      errors: response.data.errors,
      correlationId: response.data.correlationId,
    } as ApiResponse<{ financial: Patient360FinancialDto | null; claims: Patient360ClaimDto[] }>;
  },

  /**
   * Get paginated activity timeline for a patient
   */
  getActivityTimeline: async (
    programId: string,
    patientId: string,
    query?: PatientActivityTimelineQuery,
  ): Promise<ApiResponse<PatientActivityTimelineResponse>> => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.pageSize) params.set('pageSize', String(query.pageSize));
    if (query?.eventCategory) params.set('eventCategory', query.eventCategory);
    if (query?.fromDate) params.set('fromDate', query.fromDate);
    if (query?.toDate) params.set('toDate', query.toDate);
    if (query?.caseId) params.set('caseId', query.caseId);
    const qs = params.toString();
    const response = await apiClient.get<ApiResponse<PatientActivityTimelineResponse>>(
      `/programs/${programId}/patients/${patientId}/activity-timeline${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  },

  /**
   * Get smart insights for a patient (max 3, sorted by priority)
   */
  getPatientInsights: async (
    programId: string,
    patientId: string,
  ): Promise<ApiResponse<PatientInsightsResponse>> => {
    const response = await apiClient.get<ApiResponse<PatientInsightsResponse>>(
      `/programs/${programId}/patients/${patientId}/insights`,
    );
    return response.data;
  },

  /**
   * Resolve adaptive panel steps for a patient action.
   * Backend is the source of truth — returns base + injected steps.
   */
  resolvePanelSteps: async (
    programId: string,
    patientId: string,
    request: ResolvePanelStepsRequest,
  ): Promise<ApiResponse<ResolvePanelStepsResponse>> => {
    const response = await apiClient.post<ApiResponse<ResolvePanelStepsResponse>>(
      `/programs/${programId}/patients/${patientId}/panel-steps/resolve`,
      request,
    );
    return response.data;
  },
};

// ─── Legacy Export (backward compatibility) ─────────────────

export const patient360Service = {
  getPatient360: Patient360API.getPatient360,
};
